import React, { useEffect, useRef } from 'react';
import {
    createChart,
    CrosshairMode,
    CandlestickSeries,
    HistogramSeries,
    createSeriesMarkers,
    type SeriesMarker,
    type Time
} from 'lightweight-charts';
import type { BarOHLCVDetailed, Entity, TradingStrategyBacktestTrade, UserPortfolioTrade } from '@einhaus/project314-types';
import { getDate, getUnixTime } from '@einhaus/meteoric-helpers';
import { convertEstTimestampToTradingView } from '@/utils/dateHelpers.ts';
import { convertBarsToTradingView } from '@/utils/barHelpers.ts';

interface TradeChartProps {
    trade: UserPortfolioTrade | TradingStrategyBacktestTrade;
    bars: BarOHLCVDetailed[];
    entity?: Entity | null;
    duration: string;
    showVolume?: boolean; // Add showVolume prop
    height?: number;
    width?: number;
}

const TradeChart: React.FC<TradeChartProps> = ({ trade, bars, entity, duration, showVolume = true, height, width }) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const legendRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Clean up previous chart if it exists
        if (chartContainerRef.current) {
            const existingChart = (chartContainerRef.current as any).chart;

            if (existingChart) {
                existingChart.remove();
                (chartContainerRef.current as any).chart = null;

                // Clear the container
                while (chartContainerRef.current.firstChild) {
                    chartContainerRef.current.removeChild(chartContainerRef.current.firstChild);
                }
            }
        }

        if (!chartContainerRef.current || bars.length === 0) return;

        // Create chart instance
        const chart = createChart(chartContainerRef.current, {
            width: 1600,
            height: 700,
            layout: {
                background: { color: '#222' },
                textColor: '#DDD'
            },
            grid: {
                vertLines: { color: '#444' },
                horzLines: { color: '#444' }
            },
            timeScale: {
                timeVisible: true
            },
            crosshair: {
                mode: CrosshairMode.Normal
            },
            rightPriceScale: {
                scaleMargins: {
                    top: 0.1,
                    bottom: 0.2 // Increase bottom margin to prevent cutting off
                }
            }
        });

        // Store chart reference on the DOM element
        (chartContainerRef.current as any).chart = chart;

        // Center the chart by applying styles to the chart's container element
        // The chart library creates its own container inside our ref
        if (chartContainerRef.current.firstChild) {
            const chartElement = chartContainerRef.current.firstChild as HTMLElement;
            chartElement.style.margin = '0 auto';
            chartElement.style.display = 'block';
        }

        // Add candlestick series using the new API style
        const candlestickSeries = chart.addSeries(CandlestickSeries);

        // Convert bars to trading view format
        const tradingViewData = convertBarsToTradingView(bars);

        candlestickSeries.setData(tradingViewData);

        // Add volume series if showVolume is true
        if (showVolume) {
            const volumeSeries = chart.addSeries(HistogramSeries, {
                priceScaleId: '',
                color: '#26a69a'
            });

            volumeSeries.priceScale().applyOptions({
                scaleMargins: {
                    top: 0.7,
                    bottom: 0.1 // Add some bottom margin for volume display
                }
            });

            const volumeData = bars.map((bar) => ({
                time: convertEstTimestampToTradingView(bar.t),
                value: bar.v,
                color: bar.o <= bar.c ? 'rgba(0, 150, 136, 0.5)' : 'rgba(255, 82, 82, 0.5)'
            }));

            volumeSeries.setData(volumeData);
        }

        // Add transaction markers
        if (trade.transactions?.length > 0) {
            // Create markers from the transactions
            const markers: SeriesMarker<Time>[] = trade.transactions.map((transaction) => {
                // Return the properly formatted marker data
                return {
                    time: duration.endsWith('m')
                        ? convertEstTimestampToTradingView(getUnixTime(transaction.timestamp))
                        : getDate({ date: transaction.timestamp }),
                    position: 'aboveBar',
                    text: transaction.type,
                    shape: 'arrowDown',
                    color: 'white'
                };
            });

            createSeriesMarkers(candlestickSeries, markers);
        }

        // Fit chart content to show all data
        chart.timeScale().fitContent();

        // Add legend with date
        if (legendRef.current) {
            const tradeDate = 'insertDateTime' in trade ? trade.insertDateTime : trade.dateTime;

            legendRef.current.innerHTML = `
        <div class="trade-chart-legend">
          ${entity?.symbol} - ${duration} - ${getDate({ date: tradeDate, format: 'ymdhms' })}
        </div>
      `;
        }

        // Set up resize handler
        const handleResize = () => {
            if (!chartContainerRef.current) return;

            const chartInstance = (chartContainerRef.current as any).chart;
            if (!chartInstance) return;

            // Resize the chart
            chartInstance.applyOptions({
                width: chartContainerRef.current.clientWidth
            });

            // Re-center the chart
            if (chartContainerRef.current.firstChild) {
                const chartElement = chartContainerRef.current.firstChild as HTMLElement;
                chartElement.style.margin = '0 auto';
                chartElement.style.display = 'block';
            }

            // Fit content after resize
            chartInstance.timeScale().fitContent();
        };

        // Add resize listener
        window.addEventListener('resize', handleResize);

        // Clean up function
        return () => {
            window.removeEventListener('resize', handleResize);

            if (chartContainerRef.current) {
                const chartInstance = (chartContainerRef.current as any).chart;

                if (chartInstance) {
                    chartInstance.remove();
                    (chartContainerRef.current as any).chart = null;
                }
            }
        };
    }, [bars, trade, entity, duration, height, width, showVolume]);

    // No additional useEffect hooks needed - all functionality is now in the main useEffect
    return (
        <div className="trade-chart-container ">
            <div className="trade-chart-legend mb-1" ref={legendRef} style={{ textAlign: 'center' }} />
            <div
                className="trade-chart"
                ref={chartContainerRef}
                style={{
                    height: `${height}px`,
                    width: typeof width === 'string' ? width : `${width}px`,
                    marginBottom: '10px', // Add bottom margin to prevent cutting off
                    margin: '0 auto' // Center the chart horizontally
                }}
            />
        </div>
    );
};

export default TradeChart;
